/**
 * The curated set of preset profile avatars. Users pick one (no free uploads):
 * lighter on bandwidth, trivial to moderate, and cache-friendly. Each id maps
 * to an asset shipped by `@bing/assets`.
 */
export const AVATARS = [
  "fox",
  "owl",
  "lion",
  "panda",
  "cat",
  "dog",
  "tiger",
  "bear",
  "rabbit",
  "wolf",
  "eagle",
  "frog",
] as const;

export type AvatarId = (typeof AVATARS)[number];

export const DEFAULT_AVATAR: AvatarId = "fox";

export function isAvatarId(value: string): value is AvatarId {
  return (AVATARS as readonly string[]).includes(value);
}

/**
 * A COMPLETELY RANDOM preset avatar — assigned to a brand-new user at account creation so the
 * lobby/felt/friends lists aren't a wall of identical foxes. (The user can still change it in their
 * profile.) `rng` is injectable for deterministic tests; defaults to `Math.random`.
 */
export function randomAvatarId(rng: () => number = Math.random): AvatarId {
  const i = Math.min(AVATARS.length - 1, Math.floor(rng() * AVATARS.length));
  return AVATARS[i] ?? DEFAULT_AVATAR;
}

/**
 * THE SMALLER COPIES OF AN UPLOADED PHOTO — `GET /avatar/:id/:size`, `size` one of these.
 *
 * An uploaded avatar is stored ONCE, as a 512×512 JPEG, and the full image used to be what every
 * spot downloaded — a 36 px leaderboard row as much as the 104 px profile hero. These are the
 * side lengths (px) of the smaller copies the api also serves: CSS 32 / 48 / 64 at
 * {@link AVATAR_PHOTO_DPR} device pixels per CSS pixel. The api makes them ON DEMAND from the
 * stored original, so every existing avatar has them too; the original and its URL are untouched.
 *
 * ONE list for both ends: it is the web's menu (`avatarPhotoVariantFor`) AND the api's
 * allow-list — any other `:size` is a 400, so the copies an attacker can make the server compute
 * are bounded by this list, not by the integers.
 */
export const AVATAR_PHOTO_VARIANTS = [96, 144, 192] as const;

export type AvatarPhotoVariant = (typeof AVATAR_PHOTO_VARIANTS)[number];

/** Device pixels per CSS pixel the copies are sized for: a high-density phone screen. */
export const AVATAR_PHOTO_DPR = 3;

/**
 * The copy for an avatar drawn `cssSize` CSS px wide: the SMALLEST one that still has a source
 * pixel for every device pixel at {@link AVATAR_PHOTO_DPR} — or `null`, the full image, when even
 * the largest copy would be stretched (the profile heroes at 80 and 104 px, by design).
 */
export function avatarPhotoVariantFor(cssSize: number): AvatarPhotoVariant | null {
  const devicePx = Math.ceil(cssSize * AVATAR_PHOTO_DPR);
  return AVATAR_PHOTO_VARIANTS.find((side) => side >= devicePx) ?? null;
}
